/**
 * Positions the action dropdown that lives in every data-table row.
 *
 * The table sits inside several nested `overflow` containers (`.page__main`,
 * `.loggedInContent`, `.page__content`, `.table-responsive`). Bulma positions
 * `.dropdown-menu` absolutely inside the row, so those ancestors clip it: on a
 * short table, or on the last row, the menu opens outside the visible area and
 * the user has to scroll to reach it.
 *
 * The stylesheet (App.vue) switches the OPEN menu to `position: fixed` so it
 * escapes every clipping ancestor. This helper then gives it real coordinates
 * next to its trigger button, flipping above the button when there is no room
 * below. No per-view changes: it observes `is-active` toggling globally.
 *
 * Scoped to `.VueTables` — the sidenav dropdowns are left untouched.
 */

const MENU_GAP = 4;      // px between trigger and menu
const VIEWPORT_PAD = 8;  // px min distance from any viewport edge

let installed = false;
const openMenus = new Set(); // { dropdown, trigger, menu }

function triggerOf(dropdown) {
  return (
    dropdown.querySelector('.dropdown-trigger button') ||
    dropdown.querySelector('.dropdown-trigger') ||
    dropdown.querySelector('button') ||
    dropdown
  );
}

function positionMenu(entry) {
  const { trigger, menu } = entry;
  if (!menu || !trigger.isConnected) return;

  // measure at natural size
  menu.style.maxWidth = '';
  const t = trigger.getBoundingClientRect();
  const m = menu.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  const menuW = m.width || 192;
  const menuH = m.height || 0;

  // horizontal: align the menu's right edge with the trigger's right edge
  // (the Actions column is the right-most one), then clamp to the viewport.
  let left = t.right - menuW;
  left = Math.min(left, vw - menuW - VIEWPORT_PAD);
  left = Math.max(left, VIEWPORT_PAD);

  // vertical: below the trigger, or above it when it would overflow the bottom
  let top = t.bottom + MENU_GAP;
  const spaceBelow = vh - t.bottom - MENU_GAP - VIEWPORT_PAD;
  const spaceAbove = t.top - MENU_GAP - VIEWPORT_PAD;
  if (menuH > spaceBelow && spaceAbove > spaceBelow) {
    top = Math.max(VIEWPORT_PAD, t.top - MENU_GAP - menuH);
  }

  menu.style.left = Math.round(left) + 'px';
  menu.style.top = Math.round(top) + 'px';
  menu.style.right = 'auto';
  menu.style.bottom = 'auto';
  menu.style.maxWidth = Math.round(vw - 2 * VIEWPORT_PAD) + 'px';
}

function positionAll() {
  openMenus.forEach(positionMenu);
}

function refresh() {
  // rebuild the set of currently-open table dropdowns
  openMenus.clear();
  const actives = document.querySelectorAll('.VueTables .dropdown.is-active');
  actives.forEach((dropdown) => {
    const menu = dropdown.querySelector('.dropdown-menu');
    if (!menu) return;
    openMenus.add({ dropdown, trigger: triggerOf(dropdown), menu });
  });
  if (openMenus.size) {
    // measure after layout settles
    requestAnimationFrame(positionAll);
  }
}

function closeAll(except) {
  document.querySelectorAll('.VueTables .dropdown.is-active').forEach((d) => {
    if (d !== except) d.classList.remove('is-active');
  });
}

export default function initTableActionMenu() {
  if (installed || typeof window === 'undefined') return;
  if (!document.body) {
    window.addEventListener('DOMContentLoaded', initTableActionMenu, { once: true });
    return;
  }
  installed = true;

  // `is-active` is toggled by each view's own click handler; observing the
  // attribute keeps this decoupled from how that happens.
  const observer = new MutationObserver((records) => {
    let touched = false;
    for (const r of records) {
      if (
        r.type === 'attributes' &&
        r.attributeName === 'class' &&
        r.target.classList &&
        r.target.classList.contains('dropdown') &&
        r.target.closest('.VueTables')
      ) {
        touched = true;
        break;
      }
    }
    if (touched) refresh();
  });
  observer.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  // keep the menu glued to its row while any scroll container moves
  window.addEventListener('scroll', positionAll, true);
  window.addEventListener('resize', positionAll);

  // click outside closes it (the views only toggle, they never close on blur)
  document.addEventListener(
    'click',
    (e) => {
      if (!openMenus.size) return;
      if (e.target.closest && e.target.closest('.VueTables .dropdown')) {
        // let the view's handler run, then re-measure
        requestAnimationFrame(refresh);
        return;
      }
      closeAll();
    },
    true
  );

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });
}
